import { z } from "zod/v4";

import { BILLING_CYCLE_STATUSES } from "@/types/billing-cycle";

/**
 * Validation schema for the admin "edit billing cycle" dialog.
 *
 * `cycleStart` / `cycleEnd` are full ISO 8601 UTC datetimes — what the `DatePicker` emits
 * (`Date.toISOString()`) and what `PUT /api/v1/admin/billing-cycles/{id}` expects. Past instants
 * are allowed on both ends: correcting a term that has already run is the ordinary case.
 *
 * `status` is checked against the backend's own enum member names rather than a free string, so a
 * value the API would reject cannot leave the dialog. The order-of-dates rule is a superRefine
 * rather than two field rules because it belongs to neither field alone; it is attached to
 * `cycleEnd` so the message renders under the field the operator would change.
 */
export const billingCycleUpdateSchema = z
  .object({
    cycleStart: z
      .string()
      .min(1, "Start date is required")
      .refine(
        (value) => !Number.isNaN(new Date(value).getTime()),
        "Start date must be a valid date",
      ),
    cycleEnd: z
      .string()
      .min(1, "End date is required")
      .refine(
        (value) => !Number.isNaN(new Date(value).getTime()),
        "End date must be a valid date",
      ),
    status: z.enum(BILLING_CYCLE_STATUSES, {
      message: "Select a billing cycle status",
    }),
  })
  .superRefine((value, ctx) => {
    const start = new Date(value.cycleStart).getTime();
    const end = new Date(value.cycleEnd).getTime();

    if (Number.isNaN(start) || Number.isNaN(end)) {
      return;
    }

    if (end <= start) {
      ctx.addIssue({
        code: "custom",
        path: ["cycleEnd"],
        message: "End date must be after the start date",
      });
    }
  });

export type BillingCycleUpdateFormData = z.infer<typeof billingCycleUpdateSchema>;
